import { signOut } from 'aws-amplify/auth';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ANALYTICS_EVENT,
} from '@/lib/analytics/types/analytics-enums';
import { useAnalytics } from '@/hooks/analytics/use-analytics';
import * as Logger from '@/lib/monitoring/logger';
import { useDashboardSelectionStore } from '@/features/dashboard/stores/use-dashboard-selection-store';
import { useAccountActions, useAccountState } from '@/contexts/account/account-context';
import { useAuth } from '@/contexts/auth/auth-context';
import { useIntercom } from '@/contexts/intercom/intercom-context';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import { useEventsDebuggerStore } from '@/features/events/stores/use-events-debugger-store';
import { clearAllSignupProgress, useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import useStore from '@/store/app/use-store';

interface UseLogoutOptions {
  onError?: (error: unknown) => void;
}

interface LogoutCallOptions {
  redirectTo?: string;
}

function clearTemporaryFlags(): void {
  localStorage.removeItem('app-extension');
  localStorage.removeItem('app-checkout');
  localStorage.removeItem('customPixel');
}

export function useLogout(options?: UseLogoutOptions) {
  const navigate = useNavigate();
  const { track, reset: resetAnalytics } = useAnalytics();
  const { shutdown: shutdownIntercom } = useIntercom();

  const { currentAccount, accountId } = useAccountState();
  const { clearAccountData } = useAccountActions();
  const { actions: { signOut: authSignOut } } = useAuth();
  const storedUser = useStore(s => s.user);

  return useCallback(async (callOptions?: LogoutCallOptions) => {
    try {
      await authSignOut();

      track(ANALYTICS_EVENT.LOGOUT, {
        userId: storedUser?.sub,
        pixelId: currentAccount?.pixels?.[0]?.id,
        accountId: accountId,
        email: storedUser?.email,
      });

      resetAnalytics();

      clearAccountData();

      // Category A stores - CLEAR on logout (security/session data)
      useStore.getState().destroy();
      useEventsDebuggerStore.getState().destroy();
      useDashboardSelectionStore.getState().clearSelection();
      useSignupFlowStore.getState().reset();

      // Category B stores - DO NOT reset on logout (user preferences survive)
      // Onboarding, invite modal, dashboard filters, table columns, banners
      // are intentionally preserved so returning users don't re-see dismissed UI

      AccountStorage.clearAll();
      clearAllSignupProgress();
      clearTemporaryFlags();

      sessionStorage.clear();

      shutdownIntercom();

      void navigate(callOptions?.redirectTo ?? '/login');
    } catch (error) {
      Logger.error('Error signing out:', error instanceof Error ? error : null, { error });

      options?.onError?.(error);
      throw error;
    }
  }, [
    currentAccount,
    accountId,
    storedUser,
    track,
    resetAnalytics,
    authSignOut,
    clearAccountData,
    shutdownIntercom,
    navigate,
    options,
  ]);
}

export async function performLogoutCleanup(): Promise<void> {
  try {
    await signOut();
  } catch (error) {
    Logger.warn('Sign out failed during logout cleanup', { error });
  }

  // Category A stores - CLEAR on logout (security/session data)
  useStore.getState().destroy();
  useEventsDebuggerStore.getState().destroy();
  useDashboardSelectionStore.getState().clearSelection();
  useSignupFlowStore.getState().reset();

  // Category B stores - DO NOT reset on logout (user preferences survive)
  // Onboarding, invite modal, dashboard filters, table columns, banners
  // are intentionally preserved so returning users don't re-see dismissed UI

  AccountStorage.clearAll();
  clearAllSignupProgress();
  clearTemporaryFlags();

  sessionStorage.clear();
}
